import { TextInput } from '@frontend/components/basic-forms';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LuMenu, LuTrash } from 'react-icons/lu';

interface Item {
    id: string;
    value: string | undefined | null;
}

export interface DraggableListProps {
    items: Item[];
    dragCallback: (items: Item[]) => void;
    onChange: (value: string, index: number) => void;
    onRemove: (index: number) => void;
}

const DraggableList = (props: DraggableListProps) => {
    const [items, setItems] = useState<Item[]>(props.items);
    const draggedItemIndex = useRef<number | null>(null);
    const dragStartElement = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setItems(props.items);
    }, [props.items]);

    const handleDragStart = useCallback((index: number, event: React.DragEvent<HTMLDivElement>) => {
        draggedItemIndex.current = index;
        dragStartElement.current = event.currentTarget;
        event.dataTransfer.effectAllowed = 'move';
        event.currentTarget.classList.add('dragging');
        event.dataTransfer.setData('text/plain', '');
    }, []);

    const handleDragOver = useCallback(
        (targetIndex: number, event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            if (draggedItemIndex.current === null || draggedItemIndex.current === targetIndex) {
                return;
            }

            const newItems = [...items];
            const [draggedItem] = newItems.splice(draggedItemIndex.current, 1);
            newItems.splice(targetIndex, 0, draggedItem);

            setItems(newItems);
            draggedItemIndex.current = targetIndex;
        },
        [items]
    );

    const handleDragEnd = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            if (draggedItemIndex.current !== null && dragStartElement.current) {
                dragStartElement.current.classList.remove('dragging');
                draggedItemIndex.current = null;
                dragStartElement.current = null;
                props.dragCallback(items);
            }
        },
        [items, props.dragCallback]
    );

    const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const parentDiv = event.currentTarget.closest<HTMLDivElement>('.draggable');
        if (parentDiv) {
            parentDiv.draggable = true;
            parentDiv.dispatchEvent(new DragEvent('dragstart', { dataTransfer: new DataTransfer() }));
            dragStartElement.current = parentDiv;
        }
    }, []);

    const handleMouseUp = useCallback(() => {
        if (dragStartElement.current) {
            dragStartElement.current.draggable = false;
        }
    }, []);

    return (
        <div
            id='draggable-list'
            className='draggable-list flex w-full flex-col gap-2'
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}>
            {items.map((item, index) => (
                <div
                    key={item.id}
                    className={`draggable flex flex-row items-center ${draggedItemIndex.current === index ? 'dragging' : ''}`}
                    draggable='false'
                    onDragStart={(event) => handleDragStart(index, event)}
                    onDragOver={(event) => handleDragOver(index, event)}
                    onDragEnd={handleDragEnd}>
                    <div
                        data-testid={`drag-handle-${index}`}
                        className='me-2 cursor-grab'
                        onMouseDown={handleMouseDown}>
                        <LuMenu />
                    </div>
                    <TextInput
                        value={item.value}
                        id={`draggable-list-item-${index}`}
                        useConditionedStyling={false}
                        className='mb-0 flex-grow-1'
                        onChange={(v) => props.onChange(v, index)}
                    />
                    <LuTrash
                        data-testid={`remove-list-item-${index}`}
                        className='text-status-danger ml-2 size-4 cursor-pointer'
                        onClick={(e) => props.onRemove(index)}
                    />
                </div>
            ))}
        </div>
    );
};

export default DraggableList;
